import type { ReactNode } from 'react';
import type { IServiceStartingPoint } from '@common/interfaces';
import type { ServiceForSelectedServiceType } from '@common/interfaces/processStartWizard';
import { ServiceCode } from '@common/enums';
import { getServiceDataForEpisodeRadiology } from '@frontend/components/pages/processStartWizard/data/radiology';
import { getSelectedLanguage } from '@frontend/translate';
import { getServiceDataForEpisodeAllergology } from './allergology';
import { getServiceDataForEpisodeCardiology } from './cardiology';
import { getServiceDataForEpisodeDermatology } from './dermatology';
import { getServiceDataForEpisodeMentalHealth } from './mentalHealth';
import { getServiceDataForEpisodeNeurology } from './neurology';
import { getServiceDataForEpisodeOphthalmology } from './ophthalmology';
import { getServiceDataForPilot } from './pilot';
import { getServiceDataForEpisodePulmonology } from './pulmonology';
import { getServiceDataForEpisodeSomnology } from './somnology';

export interface IServiceDataItem {
    availableDuring?: string;
    buttonText: string;
    costs: string;
    costsToolTipText?: string;
    isNew?: boolean;
    isOrderedSteps: boolean;
    order?: number;
    steps: string[];
    subTitle: ReactNode;
    title: string;
    visibleStepsCount?: number;
    waitingTime: ReactNode;
}

export function getServiceData(service: ServiceForSelectedServiceType, startingPoint?: IServiceStartingPoint) {
    const translate = getSelectedLanguage();

    const defaultData: IServiceDataItem = {
        buttonText: `${translate.common.start}`,
        costs: '',
        isOrderedSteps: true,
        steps: [],
        subTitle: '',
        title: service.name,
        waitingTime: '',
    };
    switch (service.code) {
        case ServiceCode.DermaOth:
        case ServiceCode.DermaC:
        case ServiceCode.DermaSnelC:
        case ServiceCode.DermaCsc:
        case ServiceCode.DermaSnelCsc:
        case ServiceCode.DermaCwond:
            return {
                ...defaultData,
                ...getServiceDataForEpisodeDermatology(service, service.code),
            };
        case ServiceCode.Headache:
        case ServiceCode.LumbarRadiculopathy:
            return {
                ...defaultData,
                ...getServiceDataForEpisodeNeurology(service.code),
            };

        case ServiceCode.TFO:
        case ServiceCode.TOO:
        case ServiceCode.OrthO:
        case ServiceCode.OrthOFol:
            return {
                ...defaultData,
                ...getServiceDataForEpisodeOphthalmology(service, service.code),
            };

        case ServiceCode.TPO:
        case ServiceCode.TPC:
            return {
                ...defaultData,
                ...getServiceDataForEpisodePulmonology(service, service.code),
            };

        case ServiceCode.OSASV:
        case ServiceCode.OSAS:
            return {
                ...defaultData,
                ...getServiceDataForEpisodeSomnology(service, service.code),
            };

        case ServiceCode.Echography:
            return {
                ...defaultData,
                ...getServiceDataForEpisodeRadiology(),
            };

        case ServiceCode.GGZOembl:
        case ServiceCode.GGZOint:
        case ServiceCode.GGZBther:
        case ServiceCode.GGZCg:
        case ServiceCode.GGZCt:
        case ServiceCode.GGZCf2f:
        case ServiceCode.GGZCmdo:
        case ServiceCode.GGZCtriage:
        case ServiceCode.GGZv:
            return {
                ...defaultData,
                ...getServiceDataForEpisodeMentalHealth(service, service.code),
            };
        case ServiceCode.RespiAller:
            return {
                ...defaultData,
                ...getServiceDataForEpisodeAllergology(service, service.code),
            };
        case ServiceCode.TCOH:
        case ServiceCode.TCOR:
        case ServiceCode.TIECG:
        case ServiceCode.TCCh:
        case ServiceCode.TCCr:
        case ServiceCode.TCCi:
        case ServiceCode.Holter:
            return {
                ...defaultData,
                ...getServiceDataForEpisodeCardiology(service, service.code, startingPoint),
            };
        case ServiceCode.PILOT:
            return {
                ...defaultData,
                ...getServiceDataForPilot(service, service.code),
            };

        default:
            return defaultData;
    }
}
